import { useState, useEffect } from 'react';
import PageHeader from '@/components/Layout/PageHeader';
import { gstAPI } from '@/lib/api';
import { formatCurrency } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import { 
  Export, 
  FileText, 
  Calculator, 
  Upload, 
  Buildings, 
  CloudArrowUp,
  MapPin,
  Info,
} from '@phosphor-icons/react';

const MONTHS = [
  { value: '1', label: 'January' }, { value: '2', label: 'February' },
  { value: '3', label: 'March' }, { value: '4', label: 'April' },
  { value: '5', label: 'May' }, { value: '6', label: 'June' },
  { value: '7', label: 'July' }, { value: '8', label: 'August' },
  { value: '9', label: 'September' }, { value: '10', label: 'October' },
  { value: '11', label: 'November' }, { value: '12', label: 'December' },
];

export default function GSTPage() {
  const [loading, setLoading] = useState(true);
  const [month, setMonth] = useState(String(new Date().getMonth() + 1));
  const [year, setYear] = useState(String(new Date().getFullYear()));
  const [gstr1Summary, setGstr1Summary] = useState(null);
  const [gstr3bSummary, setGstr3bSummary] = useState(null);
  const [entries, setEntries] = useState([]);
  const [registrations, setRegistrations] = useState([]);
  const [returns, setReturns] = useState([]);
  const [showUpload, setShowUpload] = useState(false);
  const [uploadType, setUploadType] = useState('GSTR-1');
  const [uploadGstin, setUploadGstin] = useState('');
  const [uploadFile, setUploadFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [enrichmentStatus, setEnrichmentStatus] = useState(null);
  const [enriching, setEnriching] = useState(false);
  const [gstinQuery, setGstinQuery] = useState('');
  const [gstinResult, setGstinResult] = useState(null);

  const loadData = async () => {
    setLoading(true);
    try {
      const [gstr1Res, gstr3bRes, entriesRes, regRes, retRes, enrichRes] = await Promise.all([
        gstAPI.getGSTR1Summary(month, year),
        gstAPI.getGSTR3BSummary(month, year),
        gstAPI.listEntries({ month, year }),
        gstAPI.getRegistrations(),
        gstAPI.getReturns(),
        gstAPI.enrichmentStatus(),
      ]);
      setGstr1Summary(gstr1Res.data);
      setGstr3bSummary(gstr3bRes.data);
      setEntries(entriesRes.data.entries || []);
      setRegistrations(regRes.data.registrations || []);
      setReturns(retRes.data.returns || []);
      setEnrichmentStatus(enrichRes.data);
    } catch (error) {
      console.error('Failed to load GST data:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadData(); }, [month, year]);

  const handleUpload = async () => {
    if (!uploadFile || !uploadGstin) {
      toast.error('Select a GSTIN and file');
      return;
    }
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', uploadFile);
      if (uploadType === 'GSTR-1') {
        await gstAPI.uploadGSTR1(formData, uploadGstin);
      } else {
        await gstAPI.uploadGSTR3B(formData, uploadGstin);
      }
      toast.success(`${uploadType} uploaded successfully`);
      setShowUpload(false);
      setUploadFile(null);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const handleEnrichGST = async () => {
    setEnriching(true);
    try {
      const res = await gstAPI.enrichInvoices(100);
      toast.success(`GST enrichment started: processing ${res.data.batch_size} invoices`);
      setTimeout(() => {
        gstAPI.enrichmentStatus().then(r => setEnrichmentStatus(r.data));
        setEnriching(false);
      }, 5000);
    } catch (error) {
      toast.error('Failed to start enrichment');
      setEnriching(false);
    }
  };

  const handleVerifyGstin = async () => {
    if (!gstinQuery || gstinQuery.length !== 15) {
      toast.error('Enter a valid 15-digit GSTIN');
      return;
    }
    try {
      const res = await gstAPI.verifyGstin(gstinQuery);
      setGstinResult(res.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Verification failed');
    }
  };

  return (
    <div data-testid="gst-page">
      <PageHeader 
        title="GST Management" 
        subtitle="Multi-state GST tracking, returns, and compliance"
        actions={
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => setShowUpload(true)} data-testid="upload-gst-btn">
              <Upload size={16} className="mr-2" />
              Upload Return
            </Button>
            <Button className="bg-[#0F4C81] hover:bg-[#0B3C68]" data-testid="export-gst-btn">
              <Export size={16} className="mr-2" />
              Export
            </Button>
          </div>
        }
      />
      
      <div className="page-content">
        {/* Period Selection */}
        <div className="flex items-center gap-3 mb-6">
          <Select value={month} onValueChange={setMonth}>
            <SelectTrigger className="w-36" data-testid="month-select">
              <SelectValue placeholder="Month" />
            </SelectTrigger>
            <SelectContent>
              {MONTHS.map(m => (
                <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={year} onValueChange={setYear}>
            <SelectTrigger className="w-24" data-testid="year-select">
              <SelectValue placeholder="Year" />
            </SelectTrigger>
            <SelectContent>
              {['2024', '2025', '2026'].map(y => (
                <SelectItem key={y} value={y}>{y}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <Tabs defaultValue="summary" className="space-y-6">
          <TabsList>
            <TabsTrigger value="summary" data-testid="tab-summary">Summary</TabsTrigger>
            <TabsTrigger value="registrations" data-testid="tab-registrations">Registrations</TabsTrigger>
            <TabsTrigger value="gstr1" data-testid="tab-gstr1">GSTR-1</TabsTrigger>
            <TabsTrigger value="gstr3b" data-testid="tab-gstr3b">GSTR-3B</TabsTrigger>
            <TabsTrigger value="returns" data-testid="tab-returns">Filed Returns</TabsTrigger>
            <TabsTrigger value="entries" data-testid="tab-entries">All Entries</TabsTrigger>
            <TabsTrigger value="tools" data-testid="tab-tools">Tools</TabsTrigger>
          </TabsList>

          {/* Summary Tab */}
          <TabsContent value="summary">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="stats-card">
                <div className="flex items-center gap-3 mb-3">
                  <div className="p-2 rounded-lg bg-[#E2E8F0]">
                    <Calculator size={20} className="text-[#0F4C81]" />
                  </div>
                  <div>
                    <p className="stats-card-label">Total Output GST</p>
                    <p className="stats-card-value">{formatCurrency(gstr1Summary?.total_output_gst || 0)}</p>
                  </div>
                </div>
              </div>
              <div className="stats-card">
                <div className="flex items-center gap-3 mb-3">
                  <div className="p-2 rounded-lg bg-[#E2E8F0]">
                    <FileText size={20} className="text-[#0F4C81]" />
                  </div>
                  <div>
                    <p className="stats-card-label">B2B Invoices</p>
                    <p className="stats-card-value">{gstr1Summary?.b2b?.count || 0}</p>
                  </div>
                </div>
                <p className="text-sm text-[#64748B]">Taxable: {formatCurrency(gstr1Summary?.b2b?.taxable_amount || 0)}</p>
              </div>
              <div className="stats-card">
                <div className="flex items-center gap-3 mb-3">
                  <div className="p-2 rounded-lg bg-[#E2E8F0]">
                    <FileText size={20} className="text-[#0F4C81]" />
                  </div>
                  <div>
                    <p className="stats-card-label">Net Payable</p>
                    <p className="stats-card-value text-[#DC2626]">{formatCurrency(gstr3bSummary?.total_payable || 0)}</p>
                  </div>
                </div>
              </div>
            </div>
          </TabsContent>

          {/* Registrations Tab */}
          <TabsContent value="registrations">
            <div className="bg-white border border-[#E2E8F0] rounded-lg overflow-hidden">
              <div className="p-4 border-b border-[#E2E8F0]">
                <h3 className="font-medium font-['Plus_Jakarta_Sans'] flex items-center gap-2">
                  <Buildings size={18} className="text-[#0F4C81]" />
                  GST Registrations ({registrations.length} states)
                </h3>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-0">
                {registrations.map((reg, i) => (
                  <div key={i} className="p-4 border-b border-r border-[#E2E8F0]" data-testid={`gst-reg-${reg.state_code}`}>
                    <div className="flex items-center gap-2 mb-2">
                      <MapPin size={14} className="text-[#0F4C81]" />
                      <span className="font-medium text-sm">{reg.state}</span>
                    </div>
                    <p className="font-mono text-xs text-[#64748B]">{reg.gstin}</p>
                    <p className="text-xs text-[#64748B] mt-1">State Code: {reg.state_code}</p>
                  </div>
                ))}
              </div>
            </div>
          </TabsContent>

          {/* GSTR-1 Tab */}
          <TabsContent value="gstr1">
            <div className="bg-white border border-[#E2E8F0] rounded-lg p-6">
              <h3 className="text-base font-medium font-['Plus_Jakarta_Sans'] mb-4">GSTR-1 Summary</h3>
              <div className="space-y-4">
                <div className="grid grid-cols-3 gap-4 p-4 bg-[#F8FAFC] rounded-lg">
                  <div>
                    <p className="text-xs text-[#64748B] uppercase tracking-wider">B2B Taxable</p>
                    <p className="text-lg font-semibold">{formatCurrency(gstr1Summary?.b2b?.taxable_amount || 0)}</p>
                  </div>
                  <div>
                    <p className="text-xs text-[#64748B] uppercase tracking-wider">B2B Tax</p>
                    <p className="text-lg font-semibold">{formatCurrency(gstr1Summary?.b2b?.total_tax || 0)}</p>
                  </div>
                  <div>
                    <p className="text-xs text-[#64748B] uppercase tracking-wider">B2B Count</p>
                    <p className="text-lg font-semibold">{gstr1Summary?.b2b?.count || 0}</p>
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-4 p-4 bg-[#F8FAFC] rounded-lg">
                  <div>
                    <p className="text-xs text-[#64748B] uppercase tracking-wider">B2C Taxable</p>
                    <p className="text-lg font-semibold">{formatCurrency(gstr1Summary?.b2c?.taxable_amount || 0)}</p>
                  </div>
                  <div>
                    <p className="text-xs text-[#64748B] uppercase tracking-wider">B2C Tax</p>
                    <p className="text-lg font-semibold">{formatCurrency(gstr1Summary?.b2c?.total_tax || 0)}</p>
                  </div>
                  <div>
                    <p className="text-xs text-[#64748B] uppercase tracking-wider">B2C Count</p>
                    <p className="text-lg font-semibold">{gstr1Summary?.b2c?.count || 0}</p>
                  </div>
                </div>
              </div>
            </div>
          </TabsContent>

          {/* GSTR-3B Tab */}
          <TabsContent value="gstr3b">
            <div className="bg-white border border-[#E2E8F0] rounded-lg p-6">
              <h3 className="text-base font-medium font-['Plus_Jakarta_Sans'] mb-4">GSTR-3B Summary</h3>
              <div className="space-y-4">
                <div className="p-4 bg-[#F8FAFC] rounded-lg">
                  <h4 className="text-sm font-medium mb-3">Output GST (Liability)</h4>
                  <div className="grid grid-cols-4 gap-4">
                    <div><p className="text-xs text-[#64748B]">Taxable Value</p><p className="font-semibold">{formatCurrency(gstr3bSummary?.output_gst?.taxable_value || 0)}</p></div>
                    <div><p className="text-xs text-[#64748B]">CGST</p><p className="font-semibold">{formatCurrency(gstr3bSummary?.output_gst?.cgst || 0)}</p></div>
                    <div><p className="text-xs text-[#64748B]">SGST</p><p className="font-semibold">{formatCurrency(gstr3bSummary?.output_gst?.sgst || 0)}</p></div>
                    <div><p className="text-xs text-[#64748B]">IGST</p><p className="font-semibold">{formatCurrency(gstr3bSummary?.output_gst?.igst || 0)}</p></div>
                  </div>
                </div>
                <div className="p-4 bg-green-50 rounded-lg border border-green-200">
                  <h4 className="text-sm font-medium mb-3 text-green-800">Net Tax Payable</h4>
                  <div className="grid grid-cols-4 gap-4">
                    <div><p className="text-xs text-green-600">CGST</p><p className="font-semibold text-green-800">{formatCurrency(gstr3bSummary?.net_payable?.cgst || 0)}</p></div>
                    <div><p className="text-xs text-green-600">SGST</p><p className="font-semibold text-green-800">{formatCurrency(gstr3bSummary?.net_payable?.sgst || 0)}</p></div>
                    <div><p className="text-xs text-green-600">IGST</p><p className="font-semibold text-green-800">{formatCurrency(gstr3bSummary?.net_payable?.igst || 0)}</p></div>
                    <div><p className="text-xs text-green-600">Total</p><p className="font-bold text-lg text-green-800">{formatCurrency(gstr3bSummary?.total_payable || 0)}</p></div>
                  </div>
                </div>
              </div>
            </div>
          </TabsContent>

          {/* Filed Returns Tab */}
          <TabsContent value="returns">
            <div className="bg-white border border-[#E2E8F0] rounded-lg overflow-hidden">
              {returns.length > 0 ? (
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>Type</th>
                      <th>GSTIN</th>
                      <th>Period</th>
                      <th>Filename</th>
                      <th>Uploaded</th>
                    </tr>
                  </thead>
                  <tbody>
                    {returns.map((ret, i) => (
                      <tr key={i}>
                        <td><span className="status-pill paid">{ret.type}</span></td>
                        <td className="font-mono text-xs">{ret.gstin}</td>
                        <td>{ret.filing_period?.display || ret.period}</td>
                        <td className="text-[#64748B] text-sm">{ret.filename}</td>
                        <td className="text-[#64748B] text-sm">{ret.uploaded_at?.split('T')[0]}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <div className="p-12 text-center text-[#64748B]">
                  <FileText size={32} className="mx-auto mb-2 opacity-50" />
                  <p>No returns uploaded yet</p>
                  <Button variant="outline" className="mt-4" onClick={() => setShowUpload(true)}>
                    <Upload size={14} className="mr-2" /> Upload Return
                  </Button>
                </div>
              )}
            </div>
          </TabsContent>

          {/* Entries Tab */}
          <TabsContent value="entries">
            <div className="bg-white border border-[#E2E8F0] rounded-lg overflow-hidden">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Invoice #</th>
                    <th>Date</th>
                    <th>Place of Supply</th>
                    <th>Taxable</th>
                    <th>CGST</th>
                    <th>SGST</th>
                    <th>IGST</th>
                    <th>Total Tax</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    [...Array(10)].map((_, i) => (
                      <tr key={i}>{[...Array(8)].map((_, j) => (<td key={j}><div className="skeleton h-4 w-16 rounded" /></td>))}</tr>
                    ))
                  ) : entries.length > 0 ? (
                    entries.slice(0, 100).map((entry) => (
                      <tr key={entry.id}>
                        <td className="font-medium">{entry.invoice_number}</td>
                        <td>{entry.invoice_date}</td>
                        <td>{entry.place_of_supply || '-'}</td>
                        <td>{formatCurrency(entry.taxable_amount)}</td>
                        <td>{formatCurrency(entry.cgst)}</td>
                        <td>{formatCurrency(entry.sgst)}</td>
                        <td>{formatCurrency(entry.igst)}</td>
                        <td className="font-medium">{formatCurrency(entry.total_tax)}</td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={8} className="text-center py-8 text-[#64748B]">No GST entries for this period</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </TabsContent>

          {/* Tools Tab - GST Enrichment & GSTIN Verification */}
          <TabsContent value="tools">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* GST Enrichment */}
              <div className="bg-white border border-[#E2E8F0] rounded-lg p-5">
                <h3 className="text-lg font-semibold font-['Plus_Jakarta_Sans'] mb-3">GST Amount Enrichment</h3>
                <p className="text-sm text-[#64748B] mb-4">
                  Fetch GST amounts from Zoho individual invoice details. The bulk API omits tax breakdowns.
                </p>
                {enrichmentStatus && (
                  <div className="space-y-2 mb-4">
                    <div className="flex justify-between text-sm">
                      <span>Total Invoices</span>
                      <span className="font-medium">{enrichmentStatus.total_invoices?.toLocaleString()}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span>With GST Data</span>
                      <span className="font-medium text-green-700">{enrichmentStatus.with_gst?.toLocaleString()}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span>Missing GST</span>
                      <span className="font-medium text-[#DC2626]">{enrichmentStatus.zero_gst?.toLocaleString()}</span>
                    </div>
                    <div className="w-full bg-[#E2E8F0] rounded-full h-2 mt-2">
                      <div className="bg-[#0F4C81] h-2 rounded-full" style={{ width: `${enrichmentStatus.progress_pct}%` }} />
                    </div>
                    <p className="text-xs text-[#64748B]">{enrichmentStatus.progress_pct}% enriched</p>
                  </div>
                )}
                <Button 
                  onClick={handleEnrichGST} 
                  disabled={enriching} 
                  className="bg-[#0F4C81] hover:bg-[#0B3C68] w-full"
                  data-testid="enrich-gst-btn"
                >
                  {enriching ? 'Processing...' : 'Fetch GST from Zoho'}
                </Button>
              </div>

              {/* GSTIN Verification */}
              <div className="bg-white border border-[#E2E8F0] rounded-lg p-5">
                <h3 className="text-lg font-semibold font-['Plus_Jakarta_Sans'] mb-3">GSTIN Verification</h3>
                <p className="text-sm text-[#64748B] mb-4">
                  Verify GSTIN format and check against system records.
                </p>
                <div className="flex gap-2 mb-4">
                  <input
                    type="text"
                    value={gstinQuery}
                    onChange={(e) => setGstinQuery(e.target.value.toUpperCase())}
                    placeholder="Enter 15-digit GSTIN"
                    maxLength={15}
                    className="flex-1 border border-[#E2E8F0] rounded-lg px-3 py-2 text-sm font-mono uppercase"
                    data-testid="gstin-input"
                  />
                  <Button onClick={handleVerifyGstin} className="bg-[#0F4C81] hover:bg-[#0B3C68]" data-testid="verify-gstin-btn">
                    Verify
                  </Button>
                </div>
                {gstinResult && (
                  <div className="bg-[#F8FAFC] rounded-lg p-4 space-y-2">
                    <div className="flex justify-between text-sm">
                      <span>GSTIN</span>
                      <span className="font-mono font-medium">{gstinResult.gstin}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span>State</span>
                      <span className="font-medium">{gstinResult.state} ({gstinResult.state_code})</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span>PAN</span>
                      <span className="font-mono font-medium">{gstinResult.pan}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span>Format Valid</span>
                      <span className="font-medium text-green-700">{gstinResult.format_valid ? 'Yes' : 'No'}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span>Own Registration</span>
                      <span className={`font-medium ${gstinResult.is_own_registration ? 'text-green-700' : 'text-[#64748B]'}`}>
                        {gstinResult.is_own_registration ? 'Yes' : 'No'}
                      </span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span>Invoices in System</span>
                      <span className="font-medium">{gstinResult.invoices_in_system}</span>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </div>

      {/* Upload Dialog */}
      <Dialog open={showUpload} onOpenChange={setShowUpload}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-['Plus_Jakarta_Sans']">Upload GST Return</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="bg-[#F8FAFC] rounded-lg p-3 text-sm flex items-start gap-2">
              <Info size={16} className="text-[#0F4C81] mt-0.5 flex-shrink-0" />
              <p className="text-[#64748B]">Upload the JSON file exported from the GST Portal for GSTR-1 or GSTR-3B.</p>
            </div>
            <div>
              <label className="text-sm font-medium block mb-1.5">Return Type</label>
              <Select value={uploadType} onValueChange={setUploadType}>
                <SelectTrigger data-testid="upload-return-type">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="GSTR-1">GSTR-1</SelectItem>
                  <SelectItem value="GSTR-3B">GSTR-3B</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-sm font-medium block mb-1.5">GSTIN</label>
              <Select value={uploadGstin} onValueChange={setUploadGstin}>
                <SelectTrigger data-testid="upload-gstin-select">
                  <SelectValue placeholder="Select GSTIN" />
                </SelectTrigger>
                <SelectContent>
                  {registrations.map((reg) => (
                    <SelectItem key={reg.gstin} value={reg.gstin}>
                      {reg.state} - {reg.gstin}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-sm font-medium block mb-1.5">JSON File</label>
              <div className="border-2 border-dashed border-[#E2E8F0] rounded-lg p-6 text-center hover:border-[#0F4C81] transition-colors">
                <input type="file" accept=".json" onChange={(e) => setUploadFile(e.target.files[0])} className="hidden" id="gst-file" data-testid="upload-gst-file" />
                <label htmlFor="gst-file" className="cursor-pointer">
                  <CloudArrowUp size={32} className="mx-auto text-[#64748B] mb-2" />
                  {uploadFile ? (
                    <p className="text-sm font-medium text-[#0F4C81]">{uploadFile.name}</p>
                  ) : (
                    <p className="text-sm text-[#64748B]">Click to select JSON file</p>
                  )}
                </label>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowUpload(false)}>Cancel</Button>
            <Button onClick={handleUpload} disabled={uploading || !uploadFile || !uploadGstin} className="bg-[#0F4C81] hover:bg-[#0B3C68]" data-testid="upload-gst-submit">
              {uploading ? 'Uploading...' : 'Upload'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
